import React, { useMemo, useRef } from 'react';
import { LiliFilePickerEmptyState } from './LiliFilePickerEmptyState';
import { LiliFilePickerContent } from './LiliFilePickerContent';
import { LiliError } from '../LiliError';

export enum LiliFilePickerFileType {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  PDF = 'application/pdf',
}
export enum LiliFilePickerError {
  FILE_TOO_BIG,
  UNSUPPORTED_TYPE,
}
export interface LiliFilePickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onError'> {
  id: string;
  title: string;
  disabled?: boolean;
  onFileChange?: (file?: File) => void;
  onClick?: () => void;
  onFileClear?: () => void;
  maxFileSizeInMb?: number;
  onFileClick?: (file: File) => void;
  acceptedTypes: LiliFilePickerFileType[];
  errorMessage?: string;
  onError?: (error: LiliFilePickerError) => void;
  supportedFormatsTitle: string;
  setValue?: React.Dispatch<React.SetStateAction<File | null>>;
  value: File | null;
  shouldShowClearButton?: boolean;
  clearButtonId?: string;
  addButtonId: string;
  customIcon?: React.ReactNode;
}
export const LiliFilePicker = ({
  id,
  onFileChange,
  title,
  supportedFormatsTitle,
  maxFileSizeInMb,
  onFileClick,
  onFileClear,
  onClick,
  acceptedTypes,
  disabled,
  onError,
  value,
  setValue,
  errorMessage,
  shouldShowClearButton = true,
  clearButtonId = `${id}-clear-button`,
  addButtonId,
  customIcon,
  ...otherProps
}: LiliFilePickerProps) => {
  const filePicker = useRef<HTMLInputElement | null>(null);
  const ONE_MB_IN_BYTES: number = 1048576;
  const uploadedFileName = useMemo(() => value?.name || '', [value]);
  const typesToString = useMemo(() => acceptedTypes.join(','), [acceptedTypes]);
  const fileTypeDisplayName = (fileType: LiliFilePickerFileType) => {
    switch (fileType) {
      case LiliFilePickerFileType.JPEG:
        return 'JPEG';
      case LiliFilePickerFileType.PNG:
        return 'PNG';
      case LiliFilePickerFileType.PDF:
        return 'PDF';
      default:
        return '';
    }
  };
  const supportedTypesTitle = useMemo(() => {
    return acceptedTypes
      .map((t) => fileTypeDisplayName(t))
      .join(', ')
      .toUpperCase();
  }, [acceptedTypes]);

  const isFileTypeValid = (file: File) => {
    const fileType = file.type as LiliFilePickerFileType;
    return acceptedTypes.some((acceptedType: string) => acceptedType.includes(fileType));
  };

  const isFileSizeValid = (file: File) => {
    if (!maxFileSizeInMb) return true;
    return file.size < maxFileSizeInMb * ONE_MB_IN_BYTES;
  };

  const handleFileSelection = (file: File | null) => {
    if (!file) {
      return;
    }

    if (!isFileTypeValid(file)) {
      if (onError) {
        onError(LiliFilePickerError.UNSUPPORTED_TYPE);
      }
      return;
    }

    if (!isFileSizeValid(file)) {
      if (onError) {
        onError(LiliFilePickerError.FILE_TOO_BIG);
      }
      return;
    }

    setValue?.(file);
    if (onFileChange) {
      onFileChange(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    handleFileSelection(files.item(0));
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelection(e.target.files?.[0] || null);
  };

  const handleClearFiles = () => {
    if (filePicker?.current) {
      if ('value' in filePicker.current) {
        filePicker.current.value = '';
      }
      setValue?.(null);
      if (onFileClear) onFileClear();
    }
  };

  return (
    <div
      tabIndex={0}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      onClick={onClick}
      {...otherProps}>
      <div className='flex items-center gap-[14px] w-full h-[68px]'>
        {uploadedFileName && (
          <LiliFilePickerContent
            onClearClick={handleClearFiles}
            uploadedFileName={uploadedFileName}
            uploadedFileType={fileTypeDisplayName(value?.type as LiliFilePickerFileType)}
            clearButtonId={clearButtonId}
            shouldShowClearButton={shouldShowClearButton}
            onFileClick={() => {
              if (onFileClick && value) {
                onFileClick(value);
              }
            }}
            customIcon={customIcon}
          />
        )}
        {!uploadedFileName && (
          <LiliFilePickerEmptyState
            title={title}
            onAddClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              filePicker?.current?.click();
            }}
            disabled={disabled}
            supportedFormatsTitle={`${supportedFormatsTitle} ${supportedTypesTitle}`}
            addButtonId={addButtonId}
          />
        )}
        <input
          disabled={disabled}
          accept={typesToString}
          multiple={false}
          onChange={handleManualInput}
          ref={filePicker}
          className='hidden'
          type='file'
          id={id}
        />
      </div>
      <LiliError id={`${id}-error`} errorMessage={errorMessage} />
    </div>
  );
};
